import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Product Detail Store
 *
 * Manages product detail UI state:
 * - Selected size
 * - Active image index
 * - Reviews expansion state
 * - Product quantity
 */

interface ProductDetailStore {
  // UI States
  selectedSize: string;
  activeImageIdx: number;
  showReviewsExpanded: boolean;
  quantity: number;

  // Actions
  setSelectedSize: (size: string) => void;
  setActiveImageIdx: (idx: number) => void;
  setShowReviewsExpanded: (expanded: boolean) => void;
  toggleReviewsExpanded: () => void;
  setQuantity: (quantity: number) => void;
  incrementQuantity: () => void;
  decrementQuantity: () => void;

  // Navigation Actions
  nextImage: (totalImages: number) => void;
  prevImage: (totalImages: number) => void;

  // Reset
  reset: () => void;
}

const initialState = {
  selectedSize: "",
  activeImageIdx: 0,
  showReviewsExpanded: false,
  quantity: 1,
};

export const useProductDetailStore = create<ProductDetailStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Size Selection
      setSelectedSize: (size: string) => set({ selectedSize: size }),

      // Image Navigation
      setActiveImageIdx: (idx: number) => set({ activeImageIdx: idx }),

      nextImage: (totalImages: number) => {
        const { activeImageIdx } = get();
        set({
          activeImageIdx:
            activeImageIdx === totalImages - 1 ? 0 : activeImageIdx + 1,
        });
      },

      prevImage: (totalImages: number) => {
        const { activeImageIdx } = get();
        set({
          activeImageIdx:
            activeImageIdx === 0 ? totalImages - 1 : activeImageIdx - 1,
        });
      },

      // Reviews Expansion
      setShowReviewsExpanded: (expanded: boolean) =>
        set({ showReviewsExpanded: expanded }),

      toggleReviewsExpanded: () =>
        set((state) => ({ showReviewsExpanded: !state.showReviewsExpanded })),

      // Quantity Management
      setQuantity: (quantity: number) =>
        set({ quantity: Math.max(1, quantity) }),

      incrementQuantity: () =>
        set((state) => ({ quantity: state.quantity + 1 })),

      decrementQuantity: () =>
        set((state) => ({ quantity: Math.max(1, state.quantity - 1) })),

      // Reset
      reset: () => set(initialState),
    }),
    {
      name: "product-detail-storage",
      partialize: (state) => ({
        // Only persist these states
        selectedSize: state.selectedSize,
        quantity: state.quantity,
      }),
    }
  )
);
